import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { LockedPricing } from "../types/models";
import { useAuth } from "./AuthProvider";
import PriceLockToast from "../components/common/PriceLockToast";

interface PriceLockContextType {
  lockedPricing: LockedPricing | null;
  setLockedPricing: (pricing: LockedPricing) => void;
  clearLockedPricing: () => void;
}

const PriceLockContext = createContext<PriceLockContextType | null>(null);
const PRICE_LOCK_STORAGE_KEY = "homehelpy_price_lock";

function readStoredLockedPricing(): LockedPricing | null {
  try {
    const raw = window.sessionStorage.getItem(PRICE_LOCK_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LockedPricing>;
    if (!parsed || (parsed.lockedUntil !== null && typeof parsed.lockedUntil !== "string")) return null;

    // null lockedUntil = base rate mode (no expiry) — always valid
    if (parsed.lockedUntil === null) return parsed as LockedPricing;

    const expiresAtMs = new Date(parsed.lockedUntil).getTime();
    if (Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
      window.sessionStorage.removeItem(PRICE_LOCK_STORAGE_KEY);
      return null;
    }

    return parsed as LockedPricing;
  } catch {
    window.sessionStorage.removeItem(PRICE_LOCK_STORAGE_KEY);
    return null;
  }
}

export function PriceLockProvider({ children }: { children: ReactNode }) {
  const [lockedPricing, setLockedPricingState] = useState<LockedPricing | null>(
    readStoredLockedPricing,
  );
  const { user } = useAuth();

  const setLockedPricing = useCallback((pricing: LockedPricing) => {
    window.sessionStorage.setItem(PRICE_LOCK_STORAGE_KEY, JSON.stringify(pricing));
    setLockedPricingState(pricing);
  }, []);

  const clearLockedPricing = useCallback(() => {
    window.sessionStorage.removeItem(PRICE_LOCK_STORAGE_KEY);
    setLockedPricingState(null);
  }, []);

  // Auto-expiry: clear when lockedUntil passes (skip for base rate mode where lockedUntil is null)
  useEffect(() => {
    if (!lockedPricing || !lockedPricing.lockedUntil) return undefined;
    const expiresAtMs = new Date(lockedPricing.lockedUntil).getTime();
    const remaining = expiresAtMs - Date.now();
    if (remaining <= 0) {
      clearLockedPricing();
      return undefined;
    }
    const timer = setTimeout(clearLockedPricing, remaining);
    return () => clearTimeout(timer);
  }, [clearLockedPricing, lockedPricing]);

  // Clear lock when user signs out
  const prevUserRef = useRef(user);
  useEffect(() => {
    if (prevUserRef.current && !user) {
      clearLockedPricing();
    }
    prevUserRef.current = user;
  }, [clearLockedPricing, user]);

  return (
    <PriceLockContext.Provider value={{ lockedPricing, setLockedPricing, clearLockedPricing }}>
      {children}
      <PriceLockToast />
    </PriceLockContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePriceLock(): PriceLockContextType {
  const ctx = useContext(PriceLockContext);
  if (!ctx) throw new Error("usePriceLock must be used inside PriceLockProvider");
  return ctx;
}
